import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  defaultTab?: 'login' | 'register';
}

type Tab = 'login' | 'register' | 'forgot';

export default function AuthModal({ onClose, onSuccess, defaultTab = 'login' }: Props) {
  const [tab, setTab] = useState<Tab>(defaultTab);

  // ── Login state ────────────────────────────────────────────────────────────
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPwd, setLoginPwd] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPwd, setShowLoginPwd] = useState(false);

  // ── Register state ─────────────────────────────────────────────────────────
  const [regEmail, setRegEmail] = useState('');
  const [regPwd, setRegPwd] = useState('');
  const [regPwdConfirm, setRegPwdConfirm] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showRegPwdConfirm, setShowRegPwdConfirm] = useState(false);
  const [regTerms, setRegTerms] = useState(false);

  // ── Forgot password state ──────────────────────────────────────────────────
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    if (!loginEmail.trim()) { setLoginError('Ingrese su correo electrónico.'); return; }
    if (!loginPwd) { setLoginError('Ingrese su contraseña.'); return; }

    setLoginLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail.trim(),
      password: loginPwd,
    });
    setLoginLoading(false);

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        setLoginError('Correo o contraseña incorrectos. Verifique sus datos.');
      } else if (error.message.includes('Email not confirmed')) {
        setLoginError('Debe confirmar su correo antes de iniciar sesión. Revise su bandeja de entrada.');
      } else {
        setLoginError('Error al iniciar sesión. Intente nuevamente.');
      }
      return;
    }

    onSuccess();
    onClose();
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError('');
    if (!regEmail.trim()) { setRegError('Ingrese su correo electrónico.'); return; }
    if (!/\S+@\S+\.\S+/.test(regEmail)) { setRegError('Ingrese un correo válido.'); return; }
    if (regPwd.length < 6) { setRegError('La contraseña debe tener al menos 6 caracteres.'); return; }
    if (regPwd !== regPwdConfirm) { setRegError('Las contraseñas no coinciden.'); return; }

    setRegLoading(true);
    const { error } = await supabase.auth.signUp({
      email: regEmail.trim(),
      password: regPwd,
    });
    setRegLoading(false);

    if (error) {
      if (error.message.includes('already registered') || error.message.includes('User already registered')) {
        setRegError('Este correo ya está registrado. Inicie sesión directamente.');
      } else {
        setRegError('Error al crear la cuenta. Intente nuevamente.');
      }
      return;
    }

    setRegSuccess(true);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotEmail.trim()) { setForgotError('Ingrese su correo electrónico.'); return; }
    if (!/\S+@\S+\.\S+/.test(forgotEmail)) { setForgotError('Ingrese un correo válido.'); return; }

    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: window.location.origin,
    });
    setForgotLoading(false);

    if (error) {
      setForgotError('No se pudo enviar el correo. Intente nuevamente.');
      return;
    }

    setForgotSuccess(true);
  };

  const goToLogin = (prefillEmail?: string) => {
    if (prefillEmail) setLoginEmail(prefillEmail);
    setForgotSuccess(false);
    setForgotError('');
    setForgotEmail('');
    setTab('login');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="bg-[#0D3157] px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-xl">
              <i className={`text-white text-lg ${
                tab === 'forgot' ? 'ri-lock-password-line' : 'ri-shield-user-line'
              }`} />
            </div>
            <div>
              <h2 className="text-white font-bold text-base leading-tight">Censo de Investigadores</h2>
              <p className="text-white/60 text-xs">
                {tab === 'forgot' ? 'Recuperar contraseña' : 'UPAL — Acceso de usuarios'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Tabs — solo visibles en login y register */}
        {tab !== 'forgot' && (
          <div className="flex border-b border-slate-200">
            <button
              type="button"
              onClick={() => { setTab('login'); setLoginError(''); }}
              className={`flex-1 py-3 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                tab === 'login'
                  ? 'text-[#0D3157] border-b-2 border-[#0D3157]'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <i className="ri-login-circle-line mr-1.5" />
              Iniciar sesión
            </button>
            <button
              type="button"
              onClick={() => { setTab('register'); setRegError(''); setRegSuccess(false); }}
              className={`flex-1 py-3 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                tab === 'register'
                  ? 'text-[#0D3157] border-b-2 border-[#0D3157]'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <i className="ri-user-add-line mr-1.5" />
              Crear cuenta
            </button>
          </div>
        )}

        {/* Breadcrumb en forgot */}
        {tab === 'forgot' && (
          <div className="px-6 pt-4">
            <button
              type="button"
              onClick={() => goToLogin()}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#0D3157] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-sm" />
              Volver al inicio de sesión
            </button>
          </div>
        )}

        <div className="p-6">

          {/* ══ LOGIN TAB ══════════════════════════════════════════ */}
          {tab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  Correo electrónico
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-slate-400 text-sm" />
                  </div>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); }}
                    placeholder="usuario@upal.edu.bo"
                    autoFocus
                    className={`w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${loginError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(loginEmail);
                      setForgotError('');
                      setForgotSuccess(false);
                      setTab('forgot');
                    }}
                    className="text-xs text-[#0D3157] hover:underline font-medium cursor-pointer whitespace-nowrap"
                  >
                    ¿Olvidé mi contraseña?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-key-2-line text-slate-400 text-sm" />
                  </div>
                  <input
                    type={showLoginPwd ? 'text' : 'password'}
                    value={loginPwd}
                    onChange={(e) => { setLoginPwd(e.target.value); setLoginError(''); }}
                    placeholder="Ingrese su contraseña"
                    className={`w-full border rounded-lg pl-9 pr-10 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${loginError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
                    tabIndex={-1}
                  >
                    <i className={`text-sm ${showLoginPwd ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                  </button>
                </div>

                {loginError && (
                  <div className="flex items-start gap-1.5 mt-1.5">
                    <i className="ri-error-warning-line text-red-500 text-xs mt-0.5 shrink-0" />
                    <p className="text-red-500 text-xs leading-relaxed">{loginError}</p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full flex items-center justify-center gap-2 bg-[#0D3157] hover:bg-[#0a2444] text-white font-semibold text-sm py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              >
                {loginLoading ? (
                  <><i className="ri-loader-4-line animate-spin" /> Verificando...</>
                ) : (
                  <><i className="ri-login-circle-line" /> Iniciar sesión</>
                )}
              </button>

              <p className="text-center text-xs text-slate-400 mt-3">
                ¿No tiene cuenta?{' '}
                <button
                  type="button"
                  onClick={() => setTab('register')}
                  className="text-[#0D3157] font-semibold hover:underline cursor-pointer"
                >
                  Regístrese aquí
                </button>
              </p>
            </form>
          )}

          {/* ══ REGISTER TAB ═══════════════════════════════════════ */}
          {tab === 'register' && (
            <>
              {regSuccess ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                    <i className="ri-mail-check-line text-emerald-600 text-3xl" />
                  </div>
                  <h3 className="text-base font-bold text-[#0D3157] mb-2">¡Cuenta creada!</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-5 max-w-xs mx-auto">
                    Hemos enviado un enlace de confirmación a{' '}
                    <strong className="text-slate-700">{regEmail}</strong>. Revise su bandeja
                    de entrada y confirme su cuenta para poder iniciar sesión.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setTab('login'); setRegSuccess(false); setLoginEmail(regEmail); }}
                    className="flex items-center gap-2 mx-auto bg-[#0D3157] hover:bg-[#0a2444] text-white font-semibold text-sm px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-login-circle-line" />
                    Ir a iniciar sesión
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="flex items-start gap-3 bg-[#0D3157]/5 border border-[#0D3157]/15 rounded-lg px-4 py-3 mb-1">
                    <i className="ri-information-line text-[#0D3157] text-sm mt-0.5 shrink-0" />
                    <p className="text-[#0D3157] text-xs leading-relaxed">
                      Cree una cuenta para poder registrar sus publicaciones en el censo de investigadores UPAL.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-mail-line text-slate-400 text-sm" />
                      </div>
                      <input
                        type="email"
                        value={regEmail}
                        onChange={(e) => { setRegEmail(e.target.value); setRegError(''); }}
                        placeholder="usuario@upal.edu.bo"
                        autoFocus
                        className={`w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${regError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Contraseña <span className="text-slate-400 font-normal normal-case">(mín. 6 caracteres)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-key-2-line text-slate-400 text-sm" />
                      </div>
                      <input
                        type={showRegPwd ? 'text' : 'password'}
                        value={regPwd}
                        onChange={(e) => { setRegPwd(e.target.value); setRegError(''); }}
                        placeholder="Cree una contraseña"
                        className={`w-full border rounded-lg pl-9 pr-10 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${regError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPwd((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
                        tabIndex={-1}
                      >
                        <i className={`text-sm ${showRegPwd ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Confirmar contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-key-2-line text-slate-400 text-sm" />
                      </div>
                      <input
                        type={showRegPwdConfirm ? 'text' : 'password'}
                        value={regPwdConfirm}
                        onChange={(e) => { setRegPwdConfirm(e.target.value); setRegError(''); }}
                        placeholder="Repita la contraseña"
                        className={`w-full border rounded-lg pl-9 pr-10 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${regError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPwdConfirm((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
                        tabIndex={-1}
                      >
                        <i className={`text-sm ${showRegPwdConfirm ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                      </button>
                    </div>

                    {regError && (
                      <div className="flex items-start gap-1.5 mt-1.5">
                        <i className="ri-error-warning-line text-red-500 text-xs mt-0.5 shrink-0" />
                        <p className="text-red-500 text-xs leading-relaxed">{regError}</p>
                      </div>
                    )}
                  </div>


                  <div className="items-start gap-3 bg-[#0D3157]/5 border border-[#0D3157]/15 rounded-lg px-4 py-3 mb-1">
                  <span className="text-[#0D3157]">Consentimiento informado</span>
                  
                    <p className="text-[#0D3157] text-xs leading-relaxed">
                      Declaro haber leído y comprendido que la información registrada en la Aplicación 
                      de Censo de Investigadores será utilizada exclusivamente con fines institucionales 
                      de planificación, seguimiento y fortalecimiento de la investigación, bajo administración 
                      del Vicerrectorado de Investigación, Innovación y Desarrollo Institucional y del Comité 
                      de Investigación e Interacción. 
                    </p>
                    <p className="pt-2 text-[#0D3157] text-xs leading-relaxed">
                      Asimismo, declaro que la información consignada sobre 
                      mis publicaciones e investigaciones es veraz y respeta los principios de integridad 
                      académica, incluyendo la adecuada citación de fuentes y la prohibición del plagio o la 
                      falsificación de resultados.
                    </p>
                    
                    {regTerms ? (
                      <p className="pt-3 text-xs text-[#0D3157] text-slate-400">
                        <i className="ri-check-fill text-[#0C8D2F] text-sm mt-0.5 shrink-0" />
                        <span className="text-[#0D3157]"> Estoy de acuerdo</span>
                      </p>
                    ): (
                      <p className="pt-3 text-xs text-slate-400">
                        <button
                          onClick={() => setRegTerms(true)}
                          type="button"
                          className="text-[#0D3157] font-semibold hover:underline cursor-pointer"
                        >
                        <i className="ri-checkbox-blank-line"></i>
                        <span> Estoy de acuerdo</span>
                        </button>
                      </p>
                    )
                    }
                    
                    
                  </div>

                  <button
                    type="submit"
                    disabled={regLoading || !regTerms}
                    className="w-full flex items-center justify-center gap-2 bg-[#0D3157] hover:bg-[#0a2444] text-white font-semibold text-sm py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {regLoading ? (
                      <><i className="ri-loader-4-line animate-spin" /> Creando cuenta...</>
                    ) : (
                      <><i className="ri-user-add-line" /> Crear cuenta</>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-400">
                    ¿Ya tiene cuenta?{' '}
                    <button
                      type="button"
                      onClick={() => setTab('login')}
                      className="text-[#0D3157] font-semibold hover:underline cursor-pointer"
                    >
                      Iniciar sesión
                    </button>
                  </p>
                </form>
              )}
            </>
          )}

          {/* ══ FORGOT PASSWORD TAB ════════════════════════════════ */}
          {tab === 'forgot' && (
            <>
              {forgotSuccess ? (
                /* Estado: correo enviado exitosamente */
                <div className="text-center py-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                    <i className="ri-mail-send-line text-emerald-600 text-3xl" />
                  </div>
                  <h3 className="text-base font-bold text-[#0D3157] mb-2">
                    ¡Correo enviado!
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-2 max-w-xs mx-auto">
                    Enviamos un enlace de restablecimiento a:
                  </p>
                  <p className="text-[#0D3157] font-semibold text-sm mb-5">{forgotEmail}</p>
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-left mb-5 max-w-xs mx-auto">
                    <i className="ri-information-line text-amber-500 text-sm mt-0.5 shrink-0" />
                    <p className="text-amber-700 text-xs leading-relaxed">
                      Revise su bandeja de entrada (y la carpeta de spam). El enlace expira en 1 hora.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => goToLogin(forgotEmail)}
                    className="flex items-center gap-2 mx-auto border border-[#0D3157] text-[#0D3157] hover:bg-[#0D3157]/5 font-semibold text-sm px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-s-line" />
                    Volver al inicio de sesión
                  </button>
                </div>
              ) : (
                /* Estado: formulario de recuperación */
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="text-center mb-2">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#0D3157]/8 rounded-full mx-auto mb-3">
                      <i className="ri-lock-password-line text-[#0D3157] text-2xl" />
                    </div>
                    <h3 className="text-base font-bold text-[#0D3157] mb-1">
                      Recuperar contraseña
                    </h3>
                    <p className="text-slate-400 text-xs leading-relaxed max-w-xs mx-auto">
                      Ingrese el correo asociado a su cuenta. Le enviaremos un enlace para restablecer su contraseña.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-mail-line text-slate-400 text-sm" />
                      </div>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                        placeholder="usuario@upal.edu.bo"
                        autoFocus
                        className={`w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/30 focus:border-[#0D3157] transition-colors ${forgotError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                      />
                    </div>
                    {forgotError && (
                      <div className="flex items-start gap-1.5 mt-1.5">
                        <i className="ri-error-warning-line text-red-500 text-xs mt-0.5 shrink-0" />
                        <p className="text-red-500 text-xs leading-relaxed">{forgotError}</p>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full flex items-center justify-center gap-2 bg-[#0D3157] hover:bg-[#0a2444] text-white font-semibold text-sm py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {forgotLoading ? (
                      <><i className="ri-loader-4-line animate-spin" /> Enviando correo...</>
                    ) : (
                      <><i className="ri-mail-send-line" /> Enviar enlace de recuperación</>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-400">
                    ¿Recuerda su contraseña?{' '}
                    <button
                      type="button"
                      onClick={() => goToLogin(forgotEmail)}
                      className="text-[#0D3157] font-semibold hover:underline cursor-pointer"
                    >
                      Iniciar sesión
                    </button>
                  </p>
                </form>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}
